import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import WTMonogram from '../components/WTMonogram';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { OAuthLinkExisting } from '../components/OAuthLinkExisting';
import AuthLegalFooter from '../components/AuthLegalFooter';
import '../styles/login.css';
import type { Session } from '../types/app';

interface OAuthPseudoOption {
  pseudo: string;
  pseudo_display: string;
  registered: string;
  has_photo: boolean;
}

interface OAuthSessionInfo {
  provider: string;
  email: string | null;
  displayName: string | null;
  mode: 'select' | 'create';
  pseudos: OAuthPseudoOption[];
  pseudo_count: number;
  max_pseudos_per_email: number;
  can_link_more: boolean;
  linked_pseudo: string | null;
  expiresIn: number;
}

interface ChoosePseudoProps {
  onLogin: (session: Session) => void;
}

import { BACKEND_URL } from '../config';

const DEFAULT_MAX_PSEUDOS = 5;

/**
 * Page OAuth : création d'un nouveau pseudo, choix parmi les comptes existants,
 * ou rattachement d'un compte via pseudo + mot de passe.
 */
export default function ChoosePseudo({ onLogin }: ChoosePseudoProps) {
  const [searchParams] = useSearchParams();
  const [pseudo, setPseudo] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [pseudoAvailable, setPseudoAvailable] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionInfo, setSessionInfo] = useState<OAuthSessionInfo | null>(null);
  const [selectingPseudo, setSelectingPseudo] = useState<string | null>(null);

  const sessionId = searchParams.get('session') || '';
  const modeParam = searchParams.get('mode') || '';
  const emailParam = searchParams.get('email') || '';
  const name = searchParams.get('name') || '';

  const isSelectMode = modeParam === 'select' || sessionInfo?.mode === 'select';
  const maxPseudos = sessionInfo?.max_pseudos_per_email ?? DEFAULT_MAX_PSEUDOS;
  const pseudoCount = sessionInfo?.pseudo_count ?? sessionInfo?.pseudos?.length ?? 0;
  const canLinkMore = sessionInfo?.can_link_more ?? true;

  useEffect(() => {
    if (!sessionId) {
      setSessionLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/oauth/session/${encodeURIComponent(sessionId)}`);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || 'Session invalide');
        }
        if (!cancelled) {
          setSessionInfo(data as OAuthSessionInfo);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Session invalide ou expirée');
        }
      } finally {
        if (!cancelled) {
          setSessionLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    if (isSelectMode || !name) return;
    const parts = name.split(' ');
    if (parts.length >= 2) {
      setPrenom(parts[0]);
      setNom(parts.slice(1).join(' '));
    } else {
      setPrenom(name);
    }
  }, [name, isSelectMode]);

  useEffect(() => {
    if (isSelectMode || pseudo.length < 3) {
      setPseudoAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/check-pseudo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pseudo }),
        });
        const data = await res.json();
        setPseudoAvailable(data.available ?? false);
      } catch {
        setPseudoAvailable(null);
      } finally {
        setChecking(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [pseudo, isSelectMode]);

  const handleSelectPseudo = async (chosenPseudo: string) => {
    setError('');
    setSelectingPseudo(chosenPseudo);
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/oauth/select-pseudo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          pseudo: chosenPseudo,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const messages: Record<string, string> = {
          invalid_session: 'Session expirée. Veuillez recommencer la connexion.',
          session_expired: 'Session expirée. Veuillez recommencer la connexion.',
          pseudo_not_allowed: 'Ce compte n\'est pas associé à votre adresse email.',
        };
        throw new Error(messages[data.error] || data.message || 'Erreur lors de la connexion');
      }

      onLogin({
        pseudo: data.user.pseudo,
        pseudoDisplay: data.user.pseudo_display || data.user.pseudo,
        compet: false,
        token: data.token,
        hashPass: data.hashPass || data.hash_pass,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la connexion');
      setSelectingPseudo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/oauth/complete-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          pseudo: pseudo.trim(),
          nom: nom.trim() || undefined,
          prenom: prenom.trim() || undefined,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const messages: Record<string, string> = {
          invalid_session: 'Session expirée. Veuillez recommencer la connexion.',
          session_expired: 'Session expirée. Veuillez recommencer la connexion.',
          pseudo_taken: 'Ce pseudo est déjà utilisé. Essayez-en un autre.',
          invalid_payload: 'Données invalides.',
        };
        throw new Error(messages[data.error] || data.message || 'Erreur lors de l\'inscription');
      }

      onLogin({
        pseudo: data.user.pseudo,
        pseudoDisplay: data.user.pseudo_display || data.user.pseudo,
        compet: false,
        token: data.token,
        hashPass: data.hashPass || data.hash_pass,
        isNewAccount: true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  if (!sessionId) {
    return (
      <div className="login-page">
        <div className="login-bg" />
        <div className="login-card">
          <div className="login-header">
            <WTMonogram />
            <h1 className="login-title">Webtarot</h1>
            <p className="login-tagline">Connexion</p>
          </div>
          <div className="oauth-callback-content">
            <div className="login-error">Session invalide ou expirée</div>
            <a href="/" className="oauth-back-link">Retour à la page de connexion</a>
          </div>
        </div>
      </div>
    );
  }

  if (sessionLoading) {
    return (
      <div className="login-page">
        <div className="login-bg" />
        <div className="login-card login-card-wide">
          <div className="oauth-callback-content">
            <div className="loading-spinner" />
            <p className="oauth-status-text">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  const displayEmail = sessionInfo?.email || emailParam;
  const pseudos = sessionInfo?.pseudos ?? [];
  const providerLabel =
    sessionInfo?.provider === 'google' ? 'Google'
      : sessionInfo?.provider === 'facebook' ? 'Facebook'
        : sessionInfo?.provider === 'apple' ? 'Apple'
          : 'votre compte';

  const linkExistingBlock = (
    <OAuthLinkExisting
      sessionId={sessionId}
      canLink={canLinkMore}
      pseudoCount={pseudoCount}
      maxPseudos={maxPseudos}
      providerLabel={providerLabel}
      onLogin={onLogin}
      onError={setError}
      compact={isSelectMode && pseudos.length > 0}
    />
  );

  if (isSelectMode && pseudos.length > 0) {
    return (
      <div className="login-page">
        <div className="login-bg" />
        <div className="login-card login-card-wide">
          <div className="login-header">
            <WTMonogram />
            <h1 className="login-title">Webtarot</h1>
            <p className="login-tagline">Choisissez votre pseudo</p>
          </div>

          <div className="oauth-welcome">
            <p>
              Votre compte {providerLabel}
              {displayEmail ? ` (${displayEmail})` : ''} est associé à {pseudoCount} pseudo{pseudoCount > 1 ? 's' : ''}.
            </p>
            <p>Sélectionnez celui avec lequel vous souhaitez jouer.</p>
          </div>

          <div className="login-body oauth-select-body">
            {error && <div className="login-error">{error}</div>}

            <ul className="oauth-pseudo-list" role="listbox" aria-label="Pseudos disponibles">
              {pseudos.map((item) => {
                const isLinked = sessionInfo?.linked_pseudo === item.pseudo;
                const isBusy = loading && selectingPseudo === item.pseudo;

                return (
                  <li key={item.pseudo}>
                    <button
                      type="button"
                      className={`oauth-pseudo-card${isLinked ? ' oauth-pseudo-card-linked' : ''}`}
                      onClick={() => handleSelectPseudo(item.pseudo)}
                      disabled={loading}
                      role="option"
                      aria-selected={isLinked}
                    >
                      <PlayerAvatar pseudo={item.pseudo} size={56} />
                      <div className="oauth-pseudo-card-text">
                        <span className="oauth-pseudo-card-name">{item.pseudo_display}</span>
                        {isLinked && (
                          <span className="oauth-pseudo-card-badge">Dernière connexion</span>
                        )}
                      </div>
                      <span className="oauth-pseudo-card-action" aria-hidden="true">
                        {isBusy ? '…' : '→'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="oauth-section-divider">
              <span>ou</span>
            </div>

            {linkExistingBlock}

            <p className="oauth-select-hint">
              Pour utiliser un autre pseudo plus tard, déconnectez-vous puis reconnectez-vous avec {providerLabel}.
            </p>

            <div className="login-links">
              <a href="/">Annuler</a>
            </div>

            <AuthLegalFooter />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-bg" />
      <div className="login-card login-card-wide">
        <div className="login-header">
          <WTMonogram />
          <h1 className="login-title">Webtarot</h1>
          <p className="login-tagline">Votre compte Webtarot</p>
        </div>

        <div className="oauth-welcome">
          <p>
            Bienvenue ! Votre compte {providerLabel} a été authentifié
            {displayEmail ? ` (${displayEmail})` : ''}.
          </p>
          <p>Créez un nouveau pseudo ou rattachez un compte existant.</p>
        </div>

        <div className="login-body">
          {error && <div className="login-error">{error}</div>}

          <h3 className="oauth-section-heading">Créer un nouveau pseudo</h3>
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="pseudo">Pseudo *</label>
              <input
                type="text"
                id="pseudo"
                value={pseudo}
                onChange={(e) => setPseudo(e.target.value)}
                placeholder="Votre pseudo de jeu (min. 3 caractères)"
                minLength={3}
                maxLength={25}
                autoFocus
                required
              />
              {pseudo.length >= 3 && (
                <div className={`pseudo-check ${checking ? 'checking' : pseudoAvailable ? 'available' : 'taken'}`}>
                  {checking
                    ? 'Vérification...'
                    : pseudoAvailable
                      ? 'Pseudo disponible'
                      : 'Pseudo déjà utilisé'}
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group form-group-half">
                <label htmlFor="prenom">Prénom</label>
                <input
                  type="text"
                  id="prenom"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  placeholder="Prénom"
                />
              </div>
              <div className="form-group form-group-half">
                <label htmlFor="nom">Nom</label>
                <input
                  type="text"
                  id="nom"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  placeholder="Nom"
                />
              </div>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading || !pseudo.trim() || pseudo.length < 3 || pseudoAvailable === false}
            >
              {loading ? 'Création du compte...' : 'Créer mon compte'}
            </button>
          </form>

          <div className="oauth-section-divider">
            <span>ou</span>
          </div>

          {linkExistingBlock}

          <div className="login-links">
            <a href="/">Annuler et retour à la connexion</a>
          </div>

          <AuthLegalFooter />
        </div>
      </div>
    </div>
  );
}
